import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ScratchOff.css';

// ─── Prize config ────────────────────────────────────────────────────────────
const SYMBOLS = [
  { id: 'toilet',  emoji: '🚽', label: 'Toilet'  },
  { id: 'plunger', emoji: '🪠', label: 'Plunger' },
  { id: 'gem',     emoji: '💎', label: 'Gem'     },
  { id: 'crown',   emoji: '👑', label: 'Crown'   },
  { id: 'distil',  emoji: '✨', label: 'DISTIL' },
];

const PRIZES = {
  toilet:  { amount: 100,  label: '100 $DISTIL',   tier: 'common'    },
  plunger: { amount: 250,  label: '250 $DISTIL',   tier: 'uncommon'  },
  gem:     { amount: 500,  label: '500 $DISTIL',   tier: 'rare'      },
  crown:   { amount: 1000, label: '1,000 $DISTIL', tier: 'epic'      },
  distil:  { amount: 5000, label: '5,000 $DISTIL', tier: 'jackpot'   },
};

// Win odds: ~30% overall
// Weights for symbol placement — higher = appears more as non-winning filler
const SYMBOL_WEIGHTS = [40, 25, 20, 12, 3]; // toilet, plunger, gem, crown, distil

function weightedRandom() {
  const total = SYMBOL_WEIGHTS.reduce((a, b) => a + b, 0);
  let r = Math.random() * total;
  for (let i = 0; i < SYMBOLS.length; i++) {
    r -= SYMBOL_WEIGHTS[i];
    if (r <= 0) return SYMBOLS[i];
  }
  return SYMBOLS[0];
}

function generateCard() {
  // Decide if this is a winning card (~30% chance)
  const isWinner = Math.random() < 0.30;
  const isJackpot = Math.random() < 0.005; // 0.5% jackpot
  const isBigWin  = !isJackpot && Math.random() < 0.05; // 5% big win

  let grid = [];

  if (isJackpot) {
    // All 9 = distil (jackpot)
    grid = Array(9).fill(SYMBOLS[4]);
  } else if (isWinner) {
    // Pick a winning symbol
    let winSymbol;
    if (isBigWin) {
      winSymbol = SYMBOLS[Math.floor(Math.random() * 2) + 2]; // gem or crown
    } else {
      winSymbol = SYMBOLS[Math.floor(Math.random() * 2)]; // toilet or plunger
    }
    // Place exactly 3 of the winning symbol, rest random non-matching
    const positions = [0,1,2,3,4,5,6,7,8];
    const winPositions = [];
    while (winPositions.length < 3) {
      const idx = Math.floor(Math.random() * positions.length);
      winPositions.push(positions.splice(idx, 1)[0]);
    }
    grid = Array(9).fill(null).map((_, i) => {
      if (winPositions.includes(i)) return winSymbol;
      let s;
      do { s = weightedRandom(); } while (s.id === winSymbol.id);
      return s;
    });
  } else {
    // Losing card — no 3-of-a-kind
    let attempts = 0;
    do {
      grid = Array(9).fill(null).map(() => weightedRandom());
      attempts++;
    } while (attempts < 50 && hasThreeOfAKind(grid));
  }

  return grid;
}

function hasThreeOfAKind(grid) {
  const counts = {};
  grid.forEach(s => { counts[s.id] = (counts[s.id] || 0) + 1; });
  return Object.values(counts).some(c => c >= 3);
}

function getWinningSymbol(grid) {
  const counts = {};
  grid.forEach(s => { counts[s.id] = (counts[s.id] || 0) + 1; });
  for (const [id, count] of Object.entries(counts)) {
    if (count >= 3) return id;
  }
  return null;
}

function getTodayKey() {
  return new Date().toISOString().slice(0, 10);
}

// ─── Scratch Tile ─────────────────────────────────────────────────────────────
function ScratchTile({ symbol, isToiletMode, revealed, onReveal, index }) {
  const canvasRef = useRef(null);
  const isScratching = useRef(false);
  const hasRevealed = useRef(false);
  const scratchedPixels = useRef(0);
  const totalPixels = useRef(1);

  useEffect(() => {
    if (revealed) {
      hasRevealed.current = true;
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;

    // Draw scratch surface
    const grad = ctx.createLinearGradient(0, 0, W, H);
    if (isToiletMode) {
      grad.addColorStop(0, '#8B6914');
      grad.addColorStop(1, '#6B4F10');
    } else {
      grad.addColorStop(0, '#1e3a5f');
      grad.addColorStop(1, '#0f2440');
    }
    ctx.fillStyle = grad;
    ctx.roundRect(0, 0, W, H, 8);
    ctx.fill();

    // Scratch texture lines
    ctx.strokeStyle = isToiletMode ? 'rgba(255,215,0,0.15)' : 'rgba(34,211,238,0.15)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 8; i++) {
      ctx.beginPath();
      ctx.moveTo(Math.random() * W, 0);
      ctx.lineTo(Math.random() * W, H);
      ctx.stroke();
    }

    // "SCRATCH" text
    ctx.fillStyle = isToiletMode ? 'rgba(255,215,0,0.5)' : 'rgba(34,211,238,0.5)';
    ctx.font = 'bold 11px Space Grotesk, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('SCRATCH', W / 2, H / 2);

    totalPixels.current = W * H;
    scratchedPixels.current = 0;
  }, [revealed, isToiletMode]);

  const scratch = useCallback((e) => {
    if (revealed || hasRevealed.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let x, y;
    if (e.touches) {
      x = (e.touches[0].clientX - rect.left) * scaleX;
      y = (e.touches[0].clientY - rect.top) * scaleY;
    } else {
      x = (e.clientX - rect.left) * scaleX;
      y = (e.clientY - rect.top) * scaleY;
    }

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 18, 0, Math.PI * 2);
    ctx.fill();

    // Check % scratched
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let transparent = 0;
    for (let i = 3; i < imageData.data.length; i += 4) {
      if (imageData.data[i] === 0) transparent++;
    }
    const pct = transparent / (canvas.width * canvas.height);
    if (pct > 0.55 && !hasRevealed.current) {
      hasRevealed.current = true;
      // Clear fully
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      onReveal(index);
    }
  }, [revealed, index, onReveal]);

  const handleMouseDown = () => { isScratching.current = true; };
  const handleMouseUp   = () => { isScratching.current = false; };
  const handleMouseMove = (e) => { if (isScratching.current) scratch(e); };
  const handleTouchMove = (e) => { e.preventDefault(); scratch(e); };

  return (
    <div className={`scratch-tile ${revealed ? 'revealed' : ''}`}>
      {/* Symbol beneath */}
      <div className="scratch-symbol">
        <span className="scratch-emoji">{symbol.emoji}</span>
      </div>
      {/* Scratch canvas on top */}
      {!revealed && (
        <canvas
          ref={canvasRef}
          width={90}
          height={90}
          className="scratch-canvas"
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleMouseUp}
          onTouchMove={handleTouchMove}
        />
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
function ScratchOff({ isOpen, onClose, isToiletMode = false, isWalletConnected = false, onPrizeWon }) {
  const [phase, setPhase] = useState('idle'); // idle | playing | won | lost | used
  const [grid, setGrid] = useState([]);
  const [revealed, setRevealed] = useState(Array(9).fill(false));
  const [revealAll, setRevealAll] = useState(false);
  const [winSymbol, setWinSymbol] = useState(null);
  const [cardsLeft, setCardsLeft] = useState(1);
  const [confetti, setConfetti] = useState([]);

  // Check daily card state on open
  useEffect(() => {
    if (!isOpen) return;
    // Demo mode — always allow play, never show 'used'
    if (!isWalletConnected) {
      setCardsLeft(1);
      setPhase('idle');
      return;
    }
    const today = getTodayKey();
    const data = JSON.parse(localStorage.getItem('distil_scratch_data') || '{}');
    if (data.date === today) {
      setCardsLeft(data.cardsLeft ?? 1);
      if ((data.cardsLeft ?? 1) <= 0) {
        setPhase('used');
      } else {
        setPhase('idle');
      }
    } else {
      // New day — reset
      setCardsLeft(1);
      setPhase('idle');
      localStorage.setItem('distil_scratch_data', JSON.stringify({ date: today, cardsLeft: 1 }));
    }
  }, [isOpen, isWalletConnected]);

  const startGame = useCallback(() => {
    const newGrid = generateCard();
    setGrid(newGrid);
    setRevealed(Array(9).fill(false));
    setRevealAll(false);
    setWinSymbol(null);
    setPhase('playing');
  }, []);

  const handleReveal = useCallback((index) => {
    setRevealed(prev => {
      const next = [...prev];
      next[index] = true;
      // Check if all revealed
      const allDone = next.every(Boolean);
      if (allDone) {
        setTimeout(() => finishGame(next), 300);
      }
      return next;
    });
  }, [grid]);

  const finishGame = (revealState) => {
    setRevealAll(true);
    setTimeout(() => {
      const winner = getWinningSymbol(grid);
      if (winner) {
        setWinSymbol(winner);
        setPhase('won');
        spawnConfetti();
        if (isWalletConnected && onPrizeWon) onPrizeWon(PRIZES[winner]);
      } else {
        setPhase('lost');
      }
      // Deduct card — only when wallet connected (demo plays don't count)
      if (isWalletConnected) {
        const today = getTodayKey();
        const data = JSON.parse(localStorage.getItem('distil_scratch_data') || '{}');
        const newLeft = Math.max(0, (data.cardsLeft ?? 1) - 1);
        setCardsLeft(newLeft);
        localStorage.setItem('distil_scratch_data', JSON.stringify({ date: today, cardsLeft: newLeft }));
      }
    }, 400);
  };

  const revealAllTiles = () => {
    setRevealAll(true);
    setRevealed(Array(9).fill(true));
    setTimeout(() => {
      const winner = getWinningSymbol(grid);
      if (winner) {
        setWinSymbol(winner);
        setPhase('won');
        spawnConfetti();
        if (isWalletConnected && onPrizeWon) onPrizeWon(PRIZES[winner]);
      } else {
        setPhase('lost');
      }
      // Deduct card — only when wallet connected (demo plays don't count)
      if (isWalletConnected) {
        const today = getTodayKey();
        const data = JSON.parse(localStorage.getItem('distil_scratch_data') || '{}');
        const newLeft = Math.max(0, (data.cardsLeft ?? 1) - 1);
        setCardsLeft(newLeft);
        localStorage.setItem('distil_scratch_data', JSON.stringify({ date: today, cardsLeft: newLeft }));
      }
    }, 500);
  };

  const spawnConfetti = () => {
    const pieces = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.5,
      color: isToiletMode
        ? ['#FFD700', '#FFA500', '#FF8C00'][Math.floor(Math.random() * 3)]
        : ['#22d3ee', '#a855f7', '#FFD700'][Math.floor(Math.random() * 3)],
      size: Math.random() * 8 + 4,
    }));
    setConfetti(pieces);
    setTimeout(() => setConfetti([]), 3000);
  };

  const handleClose = () => {
    setPhase('idle');
    setGrid([]);
    setRevealed(Array(9).fill(false));
    setRevealAll(false);
    setWinSymbol(null);
    setConfetti([]);
    onClose();
  };

  if (!isOpen) return null;

  const prize = winSymbol ? PRIZES[winSymbol] : null;
  const tm = isToiletMode;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`scratch-backdrop ${tm ? 'toilet-mode' : ''}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            className={`scratch-modal ${tm ? 'toilet-mode' : ''}`}
            initial={{ opacity: 0, scale: 0.85, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 40 }}
            transition={{ type: 'spring', damping: 22, stiffness: 280 }}
            onClick={e => e.stopPropagation()}
          >
        {/* Confetti */}
        {confetti.map(p => (
          <motion.div
            key={p.id}
            className="confetti-piece"
            style={{ left: `${p.x}%`, background: p.color, width: p.size, height: p.size }}
            initial={{ y: -20, opacity: 1 }}
            animate={{ y: 400, opacity: 0, rotate: 360 }}
            transition={{ duration: 2, delay: p.delay, ease: 'easeIn' }}
          />
        ))}

        {/* Header */}
        <div className="scratch-header">
          <div className="scratch-title-row">
            <span className="scratch-icon">🎟️</span>
            <h2 className="scratch-title">DAILY SCRATCH CARD</h2>
          </div>
          <button className="scratch-close" onClick={handleClose}>✕</button>
        </div>

        {/* Demo mode notice */}
        {!isWalletConnected && (
          <div className="scratch-demo-notice">
            <span>👁️</span>
            <span>Demo mode — connect wallet to earn real $DISTIL rewards</span>
          </div>
        )}

        {/* Prize Legend */}
        <div className="scratch-legend">
          {Object.entries(PRIZES).map(([id, p]) => {
            const sym = SYMBOLS.find(s => s.id === id);
            return (
              <div key={id} className={`legend-item ${p.tier}`}>
                <span className="legend-sym">
                  {sym.emoji}
                </span>
                <span className="legend-label">{p.label}</span>
              </div>
            );
          })}
        </div>

        {/* Cards left indicator */}
        <div className="scratch-cards-left">
          <span className={`cards-badge ${cardsLeft > 0 ? 'available' : 'empty'}`}>
            {cardsLeft > 0 ? `${cardsLeft} card available` : 'No cards left today'}
          </span>
        </div>

        {/* ── IDLE ── */}
        {phase === 'idle' && (
          <motion.div className="scratch-state" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="scratch-card-preview">
              <div className="preview-grid">
                {Array(9).fill(null).map((_, i) => (
                  <div key={i} className="preview-tile">?</div>
                ))}
              </div>
            </div>
            <p className="scratch-hint">Match 3 symbols to win $DISTIL!</p>
            <motion.button
              className="scratch-btn primary"
              onClick={startGame}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              🎟️ Scratch Your Card
            </motion.button>
          </motion.div>
        )}

        {/* ── PLAYING ── */}
        {phase === 'playing' && (
          <motion.div className="scratch-state" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <p className="scratch-hint">Scratch to reveal — match 3 to win!</p>
            <div className="scratch-grid">
              {grid.map((symbol, i) => (
                <ScratchTile
                  key={i}
                  index={i}
                  symbol={symbol}
                  isToiletMode={tm}
                  revealed={revealed[i]}
                  onReveal={handleReveal}
                />
              ))}
            </div>
            <motion.button
              className="scratch-btn secondary"
              onClick={revealAllTiles}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Reveal All
            </motion.button>
          </motion.div>
        )}

        {/* ── WON ── */}
        {phase === 'won' && prize && (
          <motion.div
            className="scratch-state won"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', damping: 18 }}
          >
            <motion.div
              className={`win-badge ${prize.tier}`}
              animate={{ scale: [1, 1.08, 1] }}
              transition={{ duration: 0.6, repeat: 2 }}
            >
              {prize.tier === 'jackpot' ? '🏆 JACKPOT!' : prize.tier === 'epic' ? '👑 EPIC WIN!' : prize.tier === 'rare' ? '💎 RARE WIN!' : '🎉 WINNER!'}
            </motion.div>
            <div className="win-amount">
              {isWalletConnected ? prize.label : (
                <span className="win-amount-demo">
                  {prize.label}
                  <span className="win-demo-label">Connect wallet to earn</span>
                </span>
              )}
            </div>
            <div className="scratch-grid final">
              {grid.map((symbol, i) => (
                <ScratchTile
                  key={i}
                  index={i}
                  symbol={symbol}
                  isToiletMode={tm}
                  revealed={true}
                  onReveal={() => {}}
                />
              ))}
            </div>
            <p className="win-note">Rewards credited after next purification 🚽</p>
            <motion.button
              className="scratch-btn primary"
              onClick={handleClose}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              LFG! 🔥
            </motion.button>
          </motion.div>
        )}

        {/* ── LOST ── */}
        {phase === 'lost' && (
          <motion.div
            className="scratch-state lost"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="lose-icon">😤</div>
            <div className="lose-title">No Match This Time</div>
            <div className="scratch-grid final">
              {grid.map((symbol, i) => (
                <ScratchTile
                  key={i}
                  index={i}
                  symbol={symbol}
                  isToiletMode={tm}
                  revealed={true}
                  onReveal={() => {}}
                />
              ))}
            </div>
            <p className="lose-note">Keep purifying — more cards coming! Clean 10 objects for a bonus card.</p>
            <motion.button
              className="scratch-btn secondary"
              onClick={handleClose}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Back to Purifying
            </motion.button>
          </motion.div>
        )}

        {/* ── USED ── */}
        {phase === 'used' && (
          <motion.div
            className="scratch-state used"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="used-icon">🌙</div>
            <div className="used-title">Come Back Tomorrow!</div>
            <p className="used-note">Your free daily card resets at midnight.<br />Keep purifying to earn bonus cards!</p>
            <div className="earn-ways">
              <div className="earn-item">🧹 Clean 10 objects = +1 card</div>
              <div className="earn-item">🔥 7-day streak = +1 card</div>
            </div>
            <motion.button
              className="scratch-btn secondary"
              onClick={handleClose}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Got It
            </motion.button>
          </motion.div>
        )}
      </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ScratchOff;
